import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { eventService } from '../services/api';
import DateRangePicker from '../components/DateRangePicker';
import CantonDropdown from '../components/CantonDropdown';
import CategoryDropdown from '../components/CategoryDropdown';
import API_BASE_URL from '../config';
import '../styles/SubmissionPage.css';
import '../styles/CustomFileInput.css';
import '../styles/EventFilters.css';

const SubmissionPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { id: editId } = useParams();
  const isEditing = !!editId;
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventType: 'faescht',
    location: '',
    canton: 'ZH',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    minimumAge: 0,
    price: 'Gratis',
    website: ''
  });
  const [bannerImage, setBannerImage] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [currentBannerImage, setCurrentBannerImage] = useState('');
  const [removeBannerImage, setRemoveBannerImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);

  useEffect(() => {
    if (editId) {
      setInitialLoading(true);
      eventService.getEventById(editId).then(({ data }) => {
        setFormData({
          title: data.title || '',
          description: data.description || '',
          eventType: data.eventType || 'faescht',
          location: data.location || '',
          canton: data.canton || 'ZH',
          startDate: data.startDate ? data.startDate.split('T')[0] : '',
          startTime: data.startTime || '',
          endDate: data.endDate ? data.endDate.split('T')[0] : '',
          endTime: data.endTime || '',
          minimumAge: data.minimumAge || 0,
          price: data.price || 'Gratis',
          website: data.website || ''
        });
        if (data.bannerImage) {
          setCurrentBannerImage(data.bannerImage);
        }
      }).catch(() => {
        showToast('Event konnte nicht geladen werden', 'error');
      }).finally(() => {
        setInitialLoading(false);
      });
    }
  }, [editId]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBannerImage(file);
    if (bannerPreview) URL.revokeObjectURL(bannerPreview);
    setBannerPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate that end date/time is not before start date/time
    const startDateTime = new Date(`${formData.startDate}T${formData.startTime}`);
    const endDateTime = new Date(`${formData.endDate}T${formData.endTime}`);

    if (endDateTime <= startDateTime) {
      showToast('Enddatum und -zeit müssen nach Startdatum und -zeit liegen', 'error');
      setLoading(false);
      return;
    }

    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        data.append(key, formData[key]);
      });
      if (bannerImage) {
        data.append('bannerImage', bannerImage);
      } else if (removeBannerImage) {
        data.append('removeBannerImage', 'true');
      }

      if (isEditing) {
        await eventService.updateEvent(editId, data);
        showToast('Event erfolgreich aktualisiert und erneut zur Prüfung eingereicht!', 'success');
        setTimeout(() => {
          navigate(`/event/${editId}`);
        }, 2000);
      } else {
        const { data: newEvent } = await eventService.createEvent(data);
        showToast('Event erfolgreich eingereicht! Es wird von einem Administrator überprüft.', 'success');
        setTimeout(() => {
          navigate(`/event/${newEvent.id || newEvent._id}`);
        }, 2000);
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Fehler beim Einreichen des Events', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="submission-page">
        <p>Bitte melde dich an, um ein Event einzureichen.</p>
      </div>
    );
  }

  if (initialLoading) {
    return <div className="loading">Event wird geladen...</div>;
  }

  return (
    <div className="submission-page">
      <h1>{isEditing ? 'Event überarbeiten' : 'Event einreichen'}</h1>


      <form onSubmit={handleSubmit} className="event-form">
        <div className="form-group">
          <label>Titel *</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Beschreibung *</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="5"
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Kategorie *</label>
            <CategoryDropdown
              value={formData.eventType}
              onChange={(val) => setFormData({ ...formData, eventType: val })}
              allowEmpty={false}
            />
          </div>

          <div className="form-group">
            <label>Kanton *</label>
            <CantonDropdown
              value={formData.canton}
              onChange={(val) => setFormData({ ...formData, canton: val })}
              allowEmpty={false}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Ort *</label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleChange}
            placeholder="z.B. Zürich, Hallenstadion"
            required
          />
        </div>

        <div className="form-group">
          <label>Datum & Zeit *</label>
          <DateRangePicker
            startDate={formData.startDate}
            startTime={formData.startTime}
            endDate={formData.endDate}
            endTime={formData.endTime}
            onStartDateChange={(value) => setFormData({ ...formData, startDate: value })}
            onStartTimeChange={(value) => setFormData({ ...formData, startTime: value })}
            onEndDateChange={(value) => setFormData({ ...formData, endDate: value })}
            onEndTimeChange={(value) => setFormData({ ...formData, endTime: value })}
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Mindestalter</label>
            <input
              type="number"
              name="minimumAge"
              value={formData.minimumAge}
              onChange={handleChange}
              min="0"
            />
          </div>

          <div className="form-group">
            <label>Preis</label>
            <input
              type="text"
              name="price"
              value={formData.price}
              onChange={handleChange}
              placeholder="z.B. Gratis, CHF 50, CHF 30-60"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Webseite</label>
          <input
            type="url"
            name="website"
            value={formData.website}
            onChange={handleChange}
            placeholder="https://..."
          />
        </div>

        <div className="form-group">
          <label>Banner-Bild</label>
          {currentBannerImage && !removeBannerImage ? (
            <div className="current-banner">
              <img
                src={`${API_BASE_URL}${currentBannerImage}`}
                alt="Aktuelles Banner"
                className="current-banner-img"
              />
              <button
                type="button"
                className="btn-remove-banner"
                onClick={() => setRemoveBannerImage(true)}
              >
                Bild entfernen
              </button>
            </div>
          ) : (
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
              />
              {bannerPreview && (
                <div style={{ marginTop: 12 }}>
                  <img
                    src={bannerPreview}
                    alt="Vorschau"
                    style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 8, objectFit: 'cover', display: 'block' }}
                  />
                  <button
                    type="button"
                    className="btn-remove-banner"
                    style={{ marginTop: 8 }}
                    onClick={() => {
                      URL.revokeObjectURL(bannerPreview);
                      setBannerPreview('');
                      setBannerImage(null);
                    }}
                  >
                    Bild entfernen
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Wird eingereicht...' : (isEditing ? 'Änderungen einreichen' : 'Event einreichen')}
        </button>
      </form>
    </div>
  );
};

export default SubmissionPage;
